import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus, type LeadCloudFileType, type TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { getCloudflareUploadUrls, linkCloudflareFiles } from 'api/apiCloudflare';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import Editor from 'components/editors/Editor';
import { FilesCloudUpload } from 'modules/files';
import { toStoredCloudFile } from 'modules/files';
import spinnerTitle from 'components/others/spinnerTitle';
import { getTaskCardFileBuckets } from '../helpers/splitTaskFiles';
import TaskCardFiles from './TaskCardFiles';
import SolarIcon from 'components/icons/SolarIcon';
import TaskAnswerOptions from '../../shared/answers/TaskAnswerOptions';

interface TaskCardResponseTabProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
    onAnswerSubmitted?: () => void;
}

const hasHtmlContent = (html: string): boolean => {
    const text = String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    return text.length > 0;
};

export const canShowResponseTab = (
    task: TaskCardType,
    userId: number,
): { show: boolean; edit: boolean } => {
    const status = Number(task.taskStatus);
    const isExecutor = userId > 0 && userId === Number(task.taskExecutor);
    const isController = userId > 0 && userId === Number(task.taskController);

    if (status === TaskTypeStatus.INWORK && isExecutor) {
        return { show: true, edit: true };
    }
    if (status === TaskTypeStatus.COMPLETED && (isController || isExecutor)) {
        return { show: true, edit: false };
    }
    if (status === TaskTypeStatus.ACCEPTED) {
        return { show: true, edit: false };
    }
    return { show: false, edit: false };
};

const TaskCardResponseTab = ({
    task,
    onTaskUpdated,
    onAnswerSubmitted,
}: TaskCardResponseTabProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const userId = Number(auth?.user?.ID || 0);
    const { edit } = canShowResponseTab(task, userId);
    const { answerFiles: existingAnswerFiles } = getTaskCardFileBuckets(task);

    const [answerText, setAnswerText] = useState(task.taskAnswer?.text || '');
    const [answerKey, setAnswerKey] = useState<number | null>(task.answerKey ?? null);
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);
    const [submitting, setSubmitting] = useState(false);
    const answers = task.answers || [];

    useEffect(() => {
        setAnswerText(task.taskAnswer?.text || '');
        setAnswerKey(task.answerKey ?? null);
        setPendingCloudFiles([]);
        setCloudProgress(false);
    }, [task.ID, task.taskStatus, task.taskAnswer?.text, task.answerKey]);

    const answerOptions = answers.length > 0 ? (
        <section className="task-card-section mb-3">
            <div className="task-card-section__label">{t('answers.title')}</div>
            <TaskAnswerOptions
                answers={answers}
                value={edit ? answerKey : task.answerKey ?? null}
                name={`task-answer-${task.ID}`}
                readOnly={!edit}
                disabled={submitting}
                onChange={setAnswerKey}
            />
        </section>
    ) : null;

    if (!edit) {
        const text = task.taskAnswer?.text || '';
        return (
            <>
                {answerOptions}
                <section className="task-card-section mb-3">
                    <div className="task-card-section__label">{t('labelAnswer')}</div>
                    {hasHtmlContent(text) ? (
                        <div
                            className="task-card-description"
                            dangerouslySetInnerHTML={{ __html: text }}
                        />
                    ) : (
                        <div className="task-card-empty text-muted">—</div>
                    )}
                </section>

                <section className="task-card-section">
                    <div className="task-card-section__label">
                        {t('labelAnswerFiles')}
                        {existingAnswerFiles.length > 0 ? ` (${existingAnswerFiles.length})` : ''}
                    </div>
                    {existingAnswerFiles.length > 0 ? (
                        <div className="task-card-panel">
                            <TaskCardFiles taskId={task.ID} files={existingAnswerFiles} />
                        </div>
                    ) : (
                        <div className="task-card-empty">
                            <SolarIcon name="paperclip" className="me-2" />
                            {t('emptyFiles')}
                        </div>
                    )}
                </section>
            </>
        );
    }

    const uploadAnswerCloudFiles = async (): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {
            taskId: Number(task.ID || 0),
            leadId: Number(task.leadId || 0) || undefined,
            objectId: Number(task.objectId || 0) || undefined,
        });
        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(
            pendingCloudFiles,
            uploadConfigs,
            setCloudProgress,
        );

        setCloudProgress(100);
        return uploaded
            .filter((item) => item.success)
            .map((item) => toStoredCloudFile(item.config));
    };

    const handleSubmit = async () => {
        if (answers.length > 0 && (answerKey == null || !answers.some((item) => item.key === answerKey))) {
            showToast(t('errAnswerOption'), 'danger');
            return;
        }

        setSubmitting(true);
        let uploadedFiles: LeadCloudFileType[] = [];

        try {
            if (pendingCloudFiles.length) {
                setCloudProgress(1);
                uploadedFiles = await uploadAnswerCloudFiles();
                if (!uploadedFiles.length) {
                    showToast(t('common:messError'), 'danger');
                    setSubmitting(false);
                    setCloudProgress(false);
                    return;
                }

                const linked = await linkCloudflareFiles(uploadedFiles, {
                    taskId: Number(task.ID),
                    leadId: Number(task.leadId || 0) || undefined,
                    objectId: Number(task.objectId || 0) || undefined,
                });
                if (!linked || uploadedFiles.length !== pendingCloudFiles.length) {
                    showToast(t('common:messError'), 'danger');
                    setSubmitting(false);
                    setCloudProgress(false);
                    return;
                }
            }

            const fileKeys = uploadedFiles.map((f) => f.storageKey).filter(Boolean);
            const result = await apiTask.submitTaskAnswer(task.ID, {
                text: answerText,
                fileKeys,
                answerKey: answers.length > 0 ? answerKey : null,
            });

            if (!result?.stat || !result.task) {
                showToast(result?.errCode === 'ANSWER_REQUIRED' ? t('errAnswerOption') : t('common:messError'), 'danger');
                setSubmitting(false);
                setCloudProgress(false);
                return;
            }

            setPendingCloudFiles([]);
            showToast(t('messAnswerSent'), 'success');
            onTaskUpdated(result.task);
            onAnswerSubmitted?.();
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setSubmitting(false);
            setCloudProgress(false);
        }
    };

    return (
        <>
            {answerOptions}
            <section className="task-card-section mb-3">
                <div className="task-card-section__label">{t('labelAnswer')}</div>
                <Editor value={answerText} onChange={setAnswerText} disabled={submitting} />
            </section>

            <section className="task-card-section mb-3">
                <FilesCloudUpload
                    disabled={submitting}
                    progress={cloudProgress}
                    files={pendingCloudFiles}
                    setFiles={setPendingCloudFiles}
                    label={t('labelAnswerFiles')}
                    buttonLabel={t('btnAttach')}
                    uploadingLabel={t('common:buttons.uploading')}
                />
            </section>

            <div className="d-flex justify-content-end">
                <Button variant="success" disabled={submitting} onClick={() => void handleSubmit()}>
                    {spinnerTitle(
                        <>
                            <SolarIcon name="send" className="me-2" />
                            {t('btnSendAnswer')}
                        </>,
                        submitting,
                        t('common:buttons.saving'),
                    )}
                </Button>
            </div>
        </>
    );
};

export default TaskCardResponseTab;
